import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, ChevronRight, ArrowLeft, Calendar, Clock, Search, Bus, Plus, Minus } from "lucide-react";
import { CommunityEvent } from "@/types/rider";
import { format, addHours } from "date-fns";
import { QuoteDetails } from "./QuoteDetails";
import { LocationInputs } from "./LocationInputs";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { QuoteDisplay } from "./QuoteDisplay";
import { TripTypeSelection } from "./TripTypeSelection";
import { PaymentModal } from "./PaymentModal";
import { AutocompleteAddressInput } from "./AutocompleteAddressInput";
import { AddressInputStep } from "./AddressInputStep";
import { TripDetailsStep } from "./TripDetailsStep";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { EventCard } from "@/components/rider/EventCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getAssetPath } from "@/utils/assetUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { API_URL } from "@/config/api";
import { getVenueName } from "@/utils/venueUtils";
import { PresetTimeSelector, TimePreset } from "./PresetTimeSelector";
export interface AnimatedItineraryProps {
  eventName: string;
  price: number;
  from: string;
  to: string;
  setFrom: React.Dispatch<React.SetStateAction<string>>;
  setTo: React.Dispatch<React.SetStateAction<string>>;
  phoneNumber: string;
  handlePhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePayment: () => void;
  selectedEvent: CommunityEvent | null;
  numPeople: number;
  duration: number;
  isRoundTrip: boolean;
  tripDate: Date | null;
  placeId?: string | null;
  quoteId?: string | null;
  noOrganizationsInArea?: boolean;
  timezoneAbbreviation?: string;
  timezoneOffset?: string;
  // Time and date props
  formattedDate: string;
  startTime: string;
  endTime: string;
  handleStartTimeChange: (time: string) => void;
  handleEndTimeChange: (time: string) => void;
  convertTo24Hour: (time12h: string) => string;
  handleBackNavigation: () => void;
  isFromEventSelect: boolean;
  initialNotes?: string;
  // Return trip props
  returnFrom?: string;
  returnTo?: string;
  setReturnFrom?: React.Dispatch<React.SetStateAction<string>>;
  setReturnTo?: React.Dispatch<React.SetStateAction<string>>;
  // Time presets for custom event time selection
  timePresets?: TimePreset[];
  // Pricing key from URL for correct bus type lookup
  pricingKey?: string | null;
  // Duration from URL for correct duration_hours
  durationFromUrl?: number;
}
export const AnimatedItinerary = ({
  eventName,
  price,
  from,
  to,
  setFrom,
  setTo,
  phoneNumber,
  handlePhoneNumberChange,
  handlePayment,
  selectedEvent,
  numPeople,
  duration,
  isRoundTrip,
  tripDate,
  placeId,
  quoteId,
  noOrganizationsInArea,
  timezoneAbbreviation,
  timezoneOffset,
  formattedDate,
  startTime,
  endTime,
  handleStartTimeChange,
  handleEndTimeChange,
  convertTo24Hour,
  handleBackNavigation,
  isFromEventSelect,
  initialNotes = "",
  returnFrom: initialReturnFrom = "",
  returnTo: initialReturnTo = "",
  setReturnFrom,
  setReturnTo,
  timePresets,
  pricingKey,
  durationFromUrl
}: AnimatedItineraryProps) => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const [name, setName] = useState("");
  const [notes, setNotes] = useState(initialNotes);
  const [promoCode, setPromoCode] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [smsAgreement, setSmsAgreement] = useState(false);

  // Load promo code from cookie on component mount
  useEffect(() => {
    const getPromoCodeFromCookie = () => {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'promo_code') {
          return decodeURIComponent(value);
        }
      }
      return '';
    };

    const savedPromoCode = getPromoCodeFromCookie();
    if (savedPromoCode && !promoCode) {
      setPromoCode(savedPromoCode);
      console.log('Promo code loaded from cookie:', savedPromoCode);
    }
  }, []);

  // Add ref for scrolling to trip details section
  const tripDetailsRef = useRef<HTMLDivElement>(null);

  // State to control which section is visible on mobile - initially show pricing, hide trip details
  const [showTripDetails, setShowTripDetails] = useState(false);

  // New state for mobile flow steps (for both one-way and round trip)
  const [currentMobileStep, setCurrentMobileStep] = useState<'pricing' | 'address' | 'details'>('pricing');

  // Round trip specific state
  const [returnFromLocal, setReturnFromLocal] = useState(initialReturnFrom);
  const [returnToLocal, setReturnToLocal] = useState(initialReturnTo);
  const [returnStartTime, setReturnStartTime] = useState("");
  const [returnEndTime, setReturnEndTime] = useState("");

  // New state for the return to pickup checkbox - default to true for round trips
  const [returnToPickup, setReturnToPickup] = useState(isRoundTrip);

  // Countdown timer state
  const [countdownTime, setCountdownTime] = useState(300); // 5 minutes in seconds

  // Add state for collapsible trip details section
  const [isTripDetailsOpen, setIsTripDetailsOpen] = useState(false);

  // Add loading state for the API call
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-populate return pickup address with destination when round trip
  useEffect(() => {
    if (isRoundTrip && to && !initialReturnFrom) {
      setReturnFromLocal(to);
      setReturnFrom?.(to);
    }
  }, [isRoundTrip, to, initialReturnFrom, setReturnFrom]);

  // Handle return to pickup checkbox change
  useEffect(() => {
    if (returnToPickup && from) {
      setReturnToLocal(from);
      setReturnTo?.(from);
    } else if (!returnToPickup && !initialReturnTo) {
      setReturnToLocal("");
      setReturnTo?.("");
    }
  }, [returnToPickup, from, initialReturnTo, setReturnTo]);

  // Modified effect: Only auto-calculate return end time for round trips based on start time
  useEffect(() => {
    if (isRoundTrip && startTime) {
      // Calculate the return end time by adding the trip duration to the start time
      const calculatedReturnEndTime = calculateEndTime(startTime, duration);
      setReturnEndTime(calculatedReturnEndTime);
    }
  }, [isRoundTrip, startTime, duration]);

  // Countdown timer effect
  useEffect(() => {
    if (countdownTime > 0) {
      const timer = setTimeout(() => {
        setCountdownTime(countdownTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdownTime]);

  // Format countdown time as MM:SS
  const formatCountdownTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const formIsValid = name.trim() !== "" && phoneNumber.trim() !== "" && from.trim() !== "" && to.trim() !== "" && smsAgreement;

  // Format start time for display (convert from 24-hour to 12-hour format)
  const formatTimeFor12Hour = (time24: string): string => {
    if (!time24) return "";
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return time24; // Return the original if there's an error
    }
  };
  const handleApplePayClick = () => {
    if (!formIsValid) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    handlePayment();
  };
  const handleManualCardEntry = () => {
    if (!formIsValid) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    setIsPaymentModalOpen(true);
  };
  const handlePaymentSuccess = (paymentId: string) => {
    console.log("Payment successful with ID:", paymentId);
    toast({
      title: "Booking confirmed!",
      description: "Your party bus has been booked successfully"
    });
    // In a real implementation, you would save the booking details to your backend here
  };

  // Handle time input changes
  const handleStartTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    if (timeValue) {
      try {
        const time24 = convertTo24Hour(timeValue);
        handleStartTimeChange(time24);
      } catch (error) {
        console.error("Error converting start time:", error);
      }
    }
  };
  const handleEndTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    if (timeValue) {
      try {
        const time24 = convertTo24Hour(timeValue);
        handleEndTimeChange(time24);
      } catch (error) {
        console.error("Error converting end time:", error);
      }
    }
  };

  // Return trip time handlers
  const handleReturnStartTimeChange = (time: string) => {
    setReturnStartTime(time);
  };
  const handleReturnEndTimeChange = (time: string) => {
    setReturnEndTime(time);
  };

  // Get placeholder text based on whether it's from event select or not
  const getStartTimePlaceholder = () => {
    return isFromEventSelect ? "Pickup time (auto-calculated)" : "Enter pickup time (e.g., 5:00 PM)";
  };
  const getEndTimePlaceholder = () => {
    return isFromEventSelect ? "Event start time (auto-set)" : "Enter dropoff time (e.g., 8:00 PM)";
  };

  // Calculate price per person
  const pricePerPerson = Math.round(price / numPeople);

  // Get image source - prioritize event image, fallback to default party bus image
  const getImageSource = () => {
    if (selectedEvent) {
      // Try to get event-specific image
      const eventImageMap: Record<string, string> = {
        "Coachella": "RiderEvent_Coachella_Test_Image.png",
        "EDC": "RiderEvent_EDC_Test_Image.png",
        "Beyond Wonderland": "RiderEvent_Beyond_Wonderland_Test_Image.png",
        "Los Angeles Lakers": "RiderEvent_Lakers_Test_Image.png",
        "Rufus Du Sol": "RiderEvent_Rufus_Du_Sol_Test_Image.png"
      };
      const matchingEvent = Object.keys(eventImageMap).find(key => selectedEvent.name.toLowerCase().includes(key.toLowerCase()));
      if (matchingEvent) {
        return getAssetPath(eventImageMap[matchingEvent]);
      }
    }

    // Fallback to party bus image
    return getAssetPath("Default_Party_Bus.jpeg");
  };

  // Helper function to format date with timezone offset
  const formatDateTimeWithTimezone = (date: Date, time: string): string => {
    try {
      // Use local date (no UTC shift) and prefer event's timezone from starts_at
      const dateStr = format(date, "yyyy-MM-dd");

      // Extract timezone from selectedEvent.starts_at if available, else fallback to provided timezoneOffset
      const startsAt = selectedEvent?.starts_at;
      let offset = "Z";
      if (startsAt) {
        const match = startsAt.match(/(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/);
        if (match) {
          offset = match[1];
        }
      } else if (timezoneOffset) {
        offset = timezoneOffset;
      }

      // Normalize offset to "-05:00" form when not Z
      if (offset !== "Z" && !offset.includes(":")) {
        offset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
      }

      // Ensure time is in 24-hour HH:mm
      const t24 = /am|pm/i.test(time) ? convertTo24Hour(time) : time;

      // Return naive datetime - backend will localize based on event timezone
      return `${dateStr}T${t24}:00`;
    } catch (err) {
      console.error("formatDateTimeWithTimezone error:", err);
      return date.toISOString();
    }
  };

  // Handle text me payment link button click
  const handleTextMePaymentLink = async () => {
    if (!name.trim() || !phoneNumber.trim() || !smsAgreement) {
      toast({
        title: "Missing information",
        description: "Please fill out all required fields and agree to receive SMS messages",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      // Prepare the data for the API call - use quoteId instead of placeId
      // Use pricingKey from URL if available, otherwise fallback to first bus_pricing_info key
      const busType = pricingKey || (selectedEvent?.bus_pricing_info 
        ? Object.keys(selectedEvent.bus_pricing_info)[0] 
        : null);
      
      // Extract bus pricing details if available
      const busPricingDetails = busType && selectedEvent?.bus_pricing_info 
        ? selectedEvent.bus_pricing_info[busType] 
        : null;
      
      // Format date from event date or trip date
      const eventDate = selectedEvent?.date 
        || (tripDate ? tripDate.toISOString().split('T')[0] : null);
      
      // Helper function to ensure timezone offset is in correct format
      const formatTimezoneOffset = (offset: string): string => {
        if (offset.includes(':')) {
          return offset; // Already in correct format "-05:00"
        }
        // Convert "-0500" to "-05:00"
        return offset.slice(0, 3) + ':' + offset.slice(3);
      };
      
      // Combine date with start time for the datetime string (naive format for backend)
      const eventDateTime = eventDate && startTime && timezoneOffset
        ? `${eventDate}T${convertTo24Hour(startTime)}:00` // Naive datetime - backend will localize
        : eventDate;
      
      const tripData = {
        // Only include quote_id if there's no community_event_id
        ...(selectedEvent?.id 
          ? { 
              community_event_id: selectedEvent.id,
              ...(busType && { bus_type: busType }),
              ...(eventDateTime && { date: eventDateTime }),
              // Use the isRoundTrip prop directly (from URL), not busPricingDetails
              is_round_trip: isRoundTrip,
              ...(busPricingDetails?.passenger_count && { passenger_count: busPricingDetails.passenger_count }),
              // Use URL duration if available, otherwise fallback to busPricingDetails or duration prop
              duration_hours: durationFromUrl || busPricingDetails?.duration_hours || duration
            }
          : { quote_id: quoteId || `quote-${Date.now()}` }
        ),
        customer_name: name,
        mobile_number: phoneNumber,
        pickup_address: from,
        pickup_at: tripDate ? formatDateTimeWithTimezone(tripDate, startTime) : new Date().toISOString(),
        dropoff_at: tripDate ? formatDateTimeWithTimezone(tripDate, endTime) : new Date().toISOString(),
        promo_code: promoCode || "",
        notes: notes || "",
        redirect_url: `${window.location.origin}/rider-checkout`
      };
      
      console.log("DEBUG times", {
        startTime,
        endTime,
        pickup_at: tripDate ? formatDateTimeWithTimezone(tripDate, startTime) : null,
        dropoff_at: tripDate ? formatDateTimeWithTimezone(tripDate, endTime) : null,
        eventStartsAt: selectedEvent?.starts_at
      });
      
      console.log("Sending trip preparation request:", tripData);
      const response = await fetch(`${API_URL}trips/prepare/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tripData)
      });
      if (response.ok) {
        const result = await response.json();
        console.log("Trip preparation successful:", result);

        // Navigate to thank you page
        navigate('/rider-trip-thank-you', {
          state: {
            phoneNumber,
            customerName: name
          }
        });

        // Scroll to top on mobile after navigation
        if (window.innerWidth < 768) {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }, 100);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to prepare trip');
      }
    } catch (error) {
      console.error("Error preparing trip:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send payment link. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle mobile step navigation - updated to work for both one-way and round trips
  const handleMobileContinue = () => {
    if (isMobile) {
      // Add a small delay to create a smooth "tap" feeling
      setTimeout(() => {
        setCurrentMobileStep('address');
        // Scroll to top when navigating to address step
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 150);
    } else {
      handleScrollToTripDetails();
    }
  };
  const handleAddressStepNext = () => {
    setCurrentMobileStep('details');
  };
  const handleEditTripDetails = () => {
    setCurrentMobileStep('address');
  };

  // Handle email composition for quote request
  const handleRequestQuote = () => {
    // Extract state from destination address
    const getStateFromDestination = (destination: string): string => {
      const stateMatch = destination.match(/,\s*([A-Z]{2})\s*,/);
      return stateMatch ? stateMatch[1] : 'Unknown';
    };
    const state = getStateFromDestination(to);
    const subject = `New Party Bus Request in ${state}`;
    const body = `Hello! Please confirm the contents of the email below and we'll get back to you shortly.

Destination - ${to}
# of People - ${numPeople}
Number of Hours - ${duration}
Date - ${formattedDate}

Thank you!`;
    const mailtoLink = `mailto:disco@uscoachways.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  // Handle scroll to trip details section and show form
  const handleScrollToTripDetails = () => {
    setShowTripDetails(true);
    // Small delay to ensure the element is rendered before scrolling
    setTimeout(() => {
      // Scroll to top on mobile for better UX
      if (isMobile) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        tripDetailsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTimeStr: string, durationHours: number): string => {
    if (!startTimeStr) return "";
    try {
      // Parse the start time and add the duration
      const [hours, minutes] = startTimeStr.split(':').map(Number);
      const startDateTime = new Date();
      startDateTime.setHours(hours, minutes, 0);
      const endDateTime = addHours(startDateTime, durationHours);
      return format(endDateTime, "HH:mm");
    } catch (error) {
      console.error("Error calculating end time:", error);
      return "";
    }
  };

  // Show "no organizations in area" message if the flag is true
  if (noOrganizationsInArea) {
    return <div className="container mx-auto px-4 py-6 max-w-7xl bg-gray-50 min-h-screen">
        <div className="flex justify-between items-center mb-8">
          <button onClick={handleBackNavigation} className="flex items-center text-black hover:text-gray-700 transition-colors">
            <ArrowLeft className="mr-1 h-5 w-5" />
            <span>Back</span>
          </button>
          <div></div>
          <div className="w-[40px]"></div>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0 bg-white">
            <CardContent className="p-12 text-center">
              <div className="mb-8">
                <Bus className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-black mb-4">Our Instant-Quote-Service is not live in your city yet</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Click below to send us your details via email and we'll get back to you with a quote within 24 hours.
                </p>
              </div>
              
              <Button onClick={handleRequestQuote} className="w-full bg-black text-white py-6 rounded-xl text-lg hover:bg-gray-800 font-semibold transition-all duration-200 shadow-lg mb-4">
                Request Quote via Email
              </Button>
              
              <p className="text-sm text-gray-500">You're Almost Ready To Party!</p>
            </CardContent>
          </Card>
        </div>
      </div>;
  }

  // Mobile flow (for both one-way and round trip)
  if (isMobile) {
    return <AnimatePresence mode="wait">
        {currentMobileStep === 'pricing' && <motion.div key="pricing" className="container mx-auto px-4 py-6 max-w-7xl bg-gray-50 min-h-screen" initial={{
        opacity: 0,
        x: 0
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -100
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1] // iOS-like easing curve
      }}>
            <div className="flex justify-between items-center mb-8">
              <button onClick={handleBackNavigation} className="flex items-center text-black hover:text-gray-700 transition-colors">
                <ArrowLeft className="mr-1 h-5 w-5" />
                <span>Back</span>
              </button>
              <motion.h1 className="text-3xl font-bold text-center text-black" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }}> </motion.h1>
              <div className="w-[40px]"></div>
            </div>
            
            <motion.div className="space-y-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.4
        }}>
              {/* Image Container */}
              <Card className="overflow-hidden border-0 shadow-xl">
                <div className="relative h-[320px] w-full">
                  <img src={getImageSource()} alt={eventName} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {/* Overlay content on image */}
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Live Pricing</span>
                    </div>
                  </div>
                </div>
              </Card>
              
              {/* Pricing Card - Light Theme */}
              <Card className="shadow-xl border-0 bg-white">
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="text-lg text-gray-600 mb-4 font-bold">
                       We found the perfect party bus for your group! The price to go to {to} is:
                    </div>
                    <div className="text-5xl font-bold text-green-500 mb-3">${price}</div>
                    <div className="text-lg text-gray-600">or ${pricePerPerson} per person</div>
                  </div>
                  
                  {/* Features Section */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 text-lg">The bus comes with:</h4>
                      <div className="space-y-3">
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>Premium sound system & lighting</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>Comfortable seating for {numPeople} passengers</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>Climate control & entertainment system</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Additional Features */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="space-y-3">
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>24/7 Customer Support</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>Our always on-time guarantee</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                          <span>Easy to extend trip duration</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 space-y-3">
                    <motion.div whileTap={{
                  scale: 0.98
                }} transition={{
                  duration: 0.1
                }}>
                      <Button onClick={handleMobileContinue} className="w-full bg-gradient-to-r from-green-400 to-cyan-400 text-black py-4 rounded-xl text-base hover:from-green-500 hover:to-cyan-500 font-semibold transition-all duration-200 shadow-lg">
                        Book Your Bus!
                      </Button>
                    </motion.div>
                    
                    {/* Countdown Timer */}
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        This price is available for <span className="font-semibold text-neon-green">{formatCountdownTime(countdownTime)}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Age Requirement */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <p className="text-gray-800 font-medium text-center">
                  You must be 18+ to book this trip
                </p>
              </div>
            </motion.div>
          </motion.div>}

        {currentMobileStep === 'address' && <motion.div key="address" className="min-h-screen bg-gray-50" initial={{
        opacity: 0,
        x: 100
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -100
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1] // iOS-like easing curve
      }}>
            <motion.div className="flex justify-between items-center p-4" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }}>
              <button onClick={() => setCurrentMobileStep('pricing')} className="flex items-center text-black hover:text-gray-700 transition-colors">
                <ArrowLeft className="mr-1 h-5 w-5" />
                <span>Back</span>
              </button>
            </motion.div>
            
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.4
        }}>
              <AddressInputStep from={from} setFrom={setFrom} startTime={startTime} handleStartTimeChange={handleStartTimeChange} onNext={handleAddressStepNext} isRoundTrip={isRoundTrip} returnFrom={returnFromLocal} setReturnFrom={value => {
            setReturnFromLocal(value);
            setReturnFrom?.(value);
          }} returnStartTime={returnStartTime} handleReturnStartTimeChange={handleReturnStartTimeChange} returnToPickup={returnToPickup} setReturnToPickup={setReturnToPickup} returnTo={returnToLocal} setReturnTo={value => {
            setReturnToLocal(value);
            setReturnTo?.(value);
          }} timezoneAbbreviation={timezoneAbbreviation} timePresets={timePresets} />
            </motion.div>
          </motion.div>}

        {currentMobileStep === 'details' && <motion.div key="details" className="min-h-screen bg-gray-50" initial={{
        opacity: 0,
        x: 100
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -100
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1] // iOS-like easing curve
      }}>
            <motion.div className="flex justify-between items-center p-4" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }}>
              <button onClick={() => setCurrentMobileStep('address')} className="flex items-center text-black hover:text-gray-700 transition-colors">
                <ArrowLeft className="mr-1 h-5 w-5" />
                <span>Back</span>
              </button>
            </motion.div>
            
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.4
        }}>
              <TripDetailsStep duration={duration} capacity={numPeople} to={to} from={from} startTime={startTime} price={price} name={name} setName={setName} phoneNumber={phoneNumber} handlePhoneNumberChange={handlePhoneNumberChange} promoCode={promoCode} setPromoCode={setPromoCode} smsAgreement={smsAgreement} setSmsAgreement={setSmsAgreement} onTextMePaymentLink={handleTextMePaymentLink} onEditTripDetails={handleEditTripDetails} isSubmitting={isSubmitting} isRoundTrip={isRoundTrip} returnFrom={returnFromLocal} returnTo={returnToLocal} returnStartTime={returnStartTime} endTime={endTime} returnEndTime={returnEndTime} calculateEndTime={calculateEndTime} tripDate={formattedDate} timezoneAbbreviation={timezoneAbbreviation} />
            </motion.div>
          </motion.div>}
      </AnimatePresence>;
  }

  // Desktop flow (keep existing layout)
  return <div className="container mx-auto px-4 py-6 max-w-7xl bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button onClick={handleBackNavigation} className="flex items-center text-black hover:text-gray-700 transition-colors">
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>Back</span>
        </button>
        <motion.h1 className="text-3xl font-bold text-center text-black" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }}> </motion.h1>
        <div className="w-[40px]"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Image and Pricing */}
        {(!isMobile || !showTripDetails) && <div className="space-y-6">
            {/* Image Container */}
            <Card className="overflow-hidden border-0 shadow-xl">
              <div className="relative h-[320px] w-full">
                <img src={getImageSource()} alt={eventName} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {/* Overlay content on image */}
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">Live Pricing</span>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Pricing Card - Light Theme */}
            <Card className="shadow-xl border-0 bg-white">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="text-lg text-gray-600 mb-4 font-bold">
                    We found the perfect party bus for your group! The price to go to {to} is:
                  </div>
                  <div className="text-5xl font-bold text-green-500 mb-3">${price}</div>
                  <div className="text-lg text-gray-600">or ${pricePerPerson} per person</div>
                </div>
                
                {/* Features Section */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 text-lg">The bus comes with:</h4>
                    <div className="space-y-3">
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>Premium sound system & lighting</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>Comfortable seating for {numPeople} passengers</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>Climate control & entertainment system</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional Features */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="space-y-3">
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>24/7 Customer Support</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>Our always on-time guarantee</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-4 flex-shrink-0"></div>
                        <span>Easy to extend trip duration</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile-only continue button - moved here */}
                {isMobile && <div className="mt-8 space-y-3">
                    <Button onClick={handleScrollToTripDetails} className="w-full bg-gradient-to-r from-green-400 to-cyan-400 text-black py-4 rounded-xl text-base hover:from-green-500 hover:to-cyan-500 font-semibold transition-all duration-200 shadow-lg">
                      Book Your Bus!
                    </Button>
                    
                    {/* Countdown Timer */}
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        This price is available for <span className="font-semibold text-neon-green">{formatCountdownTime(countdownTime)}</span>
                      </p>
                    </div>
                  </div>}
              </CardContent>
            </Card>
            
            {/* Age Requirement */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <p className="text-gray-800 font-medium text-center">
                You must be 18+ to book this trip
              </p>
            </div>
          </div>}
        
        {/* Right Side - Form */}
        {(!isMobile || showTripDetails) && <motion.div ref={tripDetailsRef} className="flex flex-col" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }}>
            {/* Mobile-only text header */}
            {isMobile && showTripDetails && <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-black">Please Fill Out The Form Below To Continue</h2>
              </div>}
            
            <Card className="shadow-xl border-0 bg-white">
              <CardContent className="p-8">
                {/* Collapsible Trip Details Section - Replaces h2 header */}
                <Collapsible open={isTripDetailsOpen} onOpenChange={setIsTripDetailsOpen}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors mb-6">
                      <div className="flex-1">
                        <AutocompleteAddressInput value={to} onChange={setTo} placeholder="Your Destination" className="bg-transparent border-none text-black placeholder:text-gray-600 text-lg font-medium p-0 h-auto focus:ring-0" readOnly={true} />
                        <p className="text-sm text-gray-500 mt-1">View Trip Details</p>
                      </div>
                      <div className="ml-4">
                        {isTripDetailsOpen ? <Minus className="h-5 w-5 text-gray-600" /> : <Plus className="h-5 w-5 text-gray-600" />}
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-100">
                      {/* Trip Details Table */}
                      <table className="w-full">
                        <tbody>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Total Time Reserved</td>
                            <td className="text-right text-black font-semibold py-2">{duration} Hours</td>
                          </tr>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Bus Capacity</td>
                            <td className="text-right text-black font-semibold py-2">{numPeople} People</td>
                          </tr>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Destination</td>
                            <td className="text-right text-black font-semibold py-2">{getVenueName(to)}</td>
                          </tr>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Pickup Address</td>
                            <td className="text-right text-black font-semibold py-2">{from || "Not specified"}</td>
                          </tr>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Trip Date</td>
                            <td className="text-right text-black font-semibold py-2">{formattedDate}</td>
                          </tr>
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Pickup Time</td>
                            <td className="text-right text-black font-semibold py-2">at {formatTimeFor12Hour(startTime)}{timezoneAbbreviation ? ` ${timezoneAbbreviation}` : ''}</td>
                          </tr>
                          {isRoundTrip && <>
                              <tr>
                                <td className="text-left text-gray-600 font-medium py-2">Special Instructions</td>
                                <td className="text-right text-black font-semibold py-2">
                                  {returnToPickup ? `Drop off back at ${from || "Pickup Address"}` : "Custom return location"}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-left text-gray-600 font-medium py-2"></td>
                                <td className="text-right text-black font-semibold py-2">at {formatTimeFor12Hour(returnEndTime)}</td>
                              </tr>
                            </>}
                          <tr>
                            <td className="text-left text-gray-600 font-medium py-2">Trip Type</td>
                            <td className="text-right text-black font-semibold py-2">{isRoundTrip ? "Round Trip" : "One-Way"}</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      {/* Edit Trip Details Button */}
                      <div className="mt-6 text-center">
                        <button onClick={() => setIsTripDetailsOpen(false)} className="text-black font-medium underline hover:text-gray-700 transition-colors">
                          Edit Trip Details
                        </button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                
                {/* Address and Time Inputs - Outside collapsible section */}
                <div className="space-y-6">
                  {/* Way There Section */}
                  <div className="space-y-4">
                    {isRoundTrip && <h3 className="text-lg font-semibold text-black">Enter Pick Up Address & Time <span className="text-red-500">*</span></h3>}
                    
                    {timePresets && !isRoundTrip ? (
                      <>
                        <AutocompleteAddressInput value={from} onChange={setFrom} placeholder="Enter Your Pickup Location *" className={`h-12 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl ${from.trim() ? 'bg-gray-100' : 'bg-white'}`} />
                        <div>
                          <h4 className="text-sm font-medium text-gray-600 mb-2">Select Pickup Time</h4>
                          <PresetTimeSelector presets={timePresets} value={startTime} onChange={handleStartTimeChange} />
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AutocompleteAddressInput value={from} onChange={setFrom} placeholder="Enter Your Pickup Location *" className={`flex-1 h-12 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl ${from.trim() ? 'bg-gray-100' : 'bg-white'}`} />
                        <span className="text-gray-600 font-medium flex-shrink-0">at</span>
                        <input type="time" value={startTime} onChange={e => handleStartTimeChange(e.target.value)} className="w-32 h-12 px-3 bg-gray-100 border border-gray-300 text-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-shrink-0" readOnly={false} disabled={false} aria-readonly={false} />
                        {timezoneAbbreviation && <span className="text-sm text-gray-500 font-medium flex-shrink-0">{timezoneAbbreviation}</span>}
                      </div>
                    )}
                    
                    {/* Only show dropoff fields for non-round trips */}
                    {!isRoundTrip && <div className="flex items-center gap-2">
                        <AutocompleteAddressInput value={to} onChange={setTo} placeholder="Drop Off Address" className="flex-1 h-12 bg-gray-100 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl" readOnly={true} />
                        <span className="text-gray-600 font-medium flex-shrink-0">at</span>
                        <input type="time" value={endTime} onChange={e => handleEndTimeChange(e.target.value)} className="w-32 h-12 px-3 bg-gray-100 border border-gray-300 text-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-shrink-0" readOnly={false} disabled={false} aria-readonly={false} />
                        {timezoneAbbreviation && <span className="text-sm text-gray-500 font-medium flex-shrink-0">{timezoneAbbreviation}</span>}
                      </div>}
                  </div>

                  {/* Way Back Section - Only show for round trips */}
                  {isRoundTrip && <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-black">Enter Return Trip Information <span className="text-red-500">*</span></h3>
                      
                      <div className="flex items-center gap-2">
                         <AutocompleteAddressInput value={returnFromLocal} onChange={value => {
                    setReturnFromLocal(value);
                    setReturnFrom?.(value);
                  }} placeholder="Pick Up Address" className="flex-1 h-12 bg-gray-100 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl" />
                        <span className="text-gray-600 font-medium flex-shrink-0">at</span>
                        <input type="time" value={returnStartTime} onChange={e => handleReturnStartTimeChange(e.target.value)} className="w-32 h-12 px-3 bg-gray-100 border border-gray-300 text-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-shrink-0" />
                      </div>
                      
                      {/* Checkbox for returning to pickup location */}
                      <div className="flex items-center space-x-3">
                        <Checkbox id="return-to-pickup" checked={returnToPickup} onCheckedChange={checked => setReturnToPickup(checked === true)} className="border-gray-400 data-[state=checked]:bg-black data-[state=checked]:text-white" />
                         <label htmlFor="return-to-pickup" className="text-sm text-gray-700 cursor-pointer">
                           Drop me off at {from || '[Pickup Address]'}
                         </label>
                      </div>
                      
                       {!returnToPickup && <div className="flex items-center gap-2">
                            <AutocompleteAddressInput value={returnToLocal} onChange={value => {
                    setReturnToLocal(value);
                    setReturnTo?.(value);
                  }} placeholder="Drop Off Address" className="flex-1 h-12 bg-gray-100 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl" />
                           <span className="text-gray-600 font-medium flex-shrink-0">at</span>
                           <input type="time" value={returnEndTime} onChange={e => handleReturnEndTimeChange(e.target.value)} className="w-32 h-12 px-3 bg-gray-100 border border-gray-300 text-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-shrink-0" readOnly={true} />
                         </div>}
                    </div>}
                  
                  {/* Referral Code and Notes */}
                  <Input placeholder="Referral Code" value={promoCode} onChange={e => setPromoCode(e.target.value)} className="h-12 bg-gray-100 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl" />
                  
                  <Textarea placeholder="Please add any notes, including any stops you'd like to make during your trip, service requests, or other relevant information here" value={notes} onChange={e => setNotes(e.target.value)} className="bg-gray-100 border-gray-300 text-gray-800 placeholder:text-gray-500 rounded-xl resize-none text-base" rows={3} style={{
                fontSize: '16px'
              }} />

                  {/* Total Price */}
                  <div className="flex justify-between items-center pt-6 border-t border-gray-100">
                    <span className="text-xl text-gray-600 font-medium">Total Price</span>
                    <span className="text-3xl font-bold text-black">${price.toFixed(2)}</span>
                  </div>

                  {/* Payment Section */}
                  <p className="text-sm text-gray-500 mb-3">
                    <span className="text-red-500">*</span> Required fields
                  </p>
                  <div className="bg-gradient-to-r from-green-400 to-cyan-400 rounded-xl p-6 space-y-4">
                    <Input placeholder="Full Name *" value={name} onChange={e => setName(e.target.value)} className={`h-12 border-gray-300 text-black placeholder:text-gray-600 rounded-xl focus:ring-white focus:border-white ${name.trim() ? 'bg-gray-100' : 'bg-white'}`} />

                    <Input type="tel" placeholder="Enter Your Phone Number *" value={phoneNumber} onChange={handlePhoneNumberChange} className={`h-12 border-gray-300 text-black placeholder:text-gray-600 rounded-xl focus:ring-white focus:border-white ${phoneNumber.trim() ? 'bg-gray-100' : 'bg-white'}`} />

                    <Button onClick={handleTextMePaymentLink} disabled={isSubmitting || !smsAgreement} className="w-full bg-white text-black py-6 rounded-xl text-base hover:bg-gray-100 font-semibold transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                      {isSubmitting ? 'Sending...' : 'Text Me Payment Link'}
                    </Button>
                    
                    <div className="flex items-start space-x-3 pt-2">
                      <Checkbox id="sms-agreement" checked={smsAgreement} onCheckedChange={checked => setSmsAgreement(checked === true)} className="mt-1 border-gray-400 data-[state=checked]:bg-white data-[state=checked]:text-black" />
                      <label htmlFor="sms-agreement" className="text-sm text-white leading-relaxed cursor-pointer">
                        I agree to receive SMS messages from Disco regarding party bus trips and pricing. I understand that I may opt out at any time by replying STOP to any text message I receive from Disco. Frequency may vary. Msg & data rates may apply. To view our privacy policy, <a href="https://www.discoveryconciergeservices.com/privacy-policy" className="underline hover:text-gray-200" target="_blank" rel="noopener noreferrer">click here</a>.
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>}
      </div>

      <PaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} amount={price * 100} onPaymentSuccess={handlePaymentSuccess} />
    </div>;
};